import React, { useState, useEffect } from 'react';
import './UploadForm.css';

const UploadForm = ({ onSubmit }) => {
  const [file, setFile] = useState(null);
  const [productName, setProductName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [errors, setErrors] = useState({});
  const [visibleSections, setVisibleSections] = useState(['video']);

  // Progressive form disclosure - unlock sections as user completes fields
  useEffect(() => {
    setVisibleSections(prev => {
      const newSections = new Set(prev);

      // Show product section when file is selected
      if (file) {
        newSections.add('product');
      }

      // Show buyer section when product details are filled
      if (productName.trim() && companyName.trim()) {
        newSections.add('buyer');
      }

      return Array.from(newSections);
    });
  }, [file, productName, companyName]);

  const validate = () => {
    const newErrors = {};
    if (!file) newErrors.file = 'Please select a video file';
    if (!productName.trim()) newErrors.productName = 'Product name is required';
    if (!companyName.trim()) newErrors.companyName = 'Company name is required';
    if (!targetAudience.trim()) newErrors.targetAudience = 'Target audience is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validExtensions = ['.mov', '.mp4', '.avi'];
      const fileExtension = selectedFile.name.substring(selectedFile.name.lastIndexOf('.')).toLowerCase();

      if (validExtensions.includes(fileExtension)) {
        setFile(selectedFile);
        setErrors(prev => ({ ...prev, file: null }));
      } else {
        setErrors(prev => ({ ...prev, file: 'Invalid file type. Use .mov, .mp4, or .avi' }));
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      const formData = {
        file,
        productName,
        companyName,
        targetAudience
      };
      onSubmit(formData);
    }
  };

  return (
    <div className="upload-form-container">
      <div className="upload-form-card animate-slide-up">
        <h1>
          TestBox Demo Gap Analysis
        </h1>
        <p className="subtitle">
          See exactly how your demo's engineering and delivery stack up against the top 1% of the market
        </p>

        <form onSubmit={handleSubmit} className="upload-form" noValidate>
          <div className="form-group fade-in delay-1">
            <label htmlFor="video-file">Video File *</label>
            <div className={`file-input-wrapper ${errors.file ? 'error' : ''}`}>
              <input
                type="file"
                id="video-file"
                accept=".mov,.mp4,.avi"
                onChange={handleFileChange}
                className="file-input"
                aria-invalid={!!errors.file}
                aria-describedby="file-error"
              />
              <div className="file-input-button">
                {file ? (
                  <span className="file-selected">Selected: {file.name}</span>
                ) : (
                  <span>Choose Video File (.mov, .mp4, .avi)</span>
                )}
              </div>
            </div>
            {errors.file && <span id="file-error" className="error-message">{errors.file}</span>}
            <p className="micro-copy">🔒 Zero-Usage Policy: Your data is processed in an encrypted environment strictly for the purpose of this analysis. It is not used for model training, analytics, or feature development.</p>
          </div>

          {visibleSections.includes('product') && (
            <>
              <p className="helper-text fade-in delay-2">Help our AI grade this accurately.</p>

              <div className="form-row fade-in delay-2">
            <div className="form-group">
              <label htmlFor="product-name">Product Name *</label>
              <input
                type="text"
                id="product-name"
                value={productName}
                onChange={(e) => {
                  setProductName(e.target.value);
                  if (errors.productName) setErrors(prev => ({ ...prev, productName: null }));
                }}
                className={`text-input ${errors.productName ? 'error' : ''}`}
                placeholder="e.g., Mailchimp"
                aria-invalid={!!errors.productName}
              />
              {errors.productName && <span className="error-message">{errors.productName}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="company-name">Company Name *</label>
              <input
                type="text"
                id="company-name"
                value={companyName}
                onChange={(e) => {
                  setCompanyName(e.target.value);
                  if (errors.companyName) setErrors(prev => ({ ...prev, companyName: null }));
                }}
                className={`text-input ${errors.companyName ? 'error' : ''}`}
                placeholder="e.g., Shopify"
                aria-invalid={!!errors.companyName}
              />
              {errors.companyName && <span className="error-message">{errors.companyName}</span>}
            </div>
          </div>
            </>
          )}

          {visibleSections.includes('buyer') && (
            <div className="form-group fade-in delay-3">
              <label htmlFor="target-audience">Who is the buyer for this demo? *</label>
            <textarea
              id="target-audience"
              value={targetAudience}
              onChange={(e) => {
                setTargetAudience(e.target.value);
                if (errors.targetAudience) setErrors(prev => ({ ...prev, targetAudience: null }));
              }}
              className={`textarea-input ${errors.targetAudience ? 'error' : ''}`}
              rows="3"
              placeholder="e.g., Email Marketing"
              aria-invalid={!!errors.targetAudience}
            />
            {errors.targetAudience && <span className="error-message">{errors.targetAudience}</span>}
            </div>
          )}

          {visibleSections.includes('buyer') && targetAudience.trim() && (
            <button type="submit" className="submit-button fade-in delay-4">
              Analyze Demo
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default UploadForm;

